import { motion } from 'framer-motion';
import { ArrowDown, Sparkles } from 'lucide-react';
import { goToApp } from '@/lib/track';

const APP_SCREENSHOTS = {
  dashboard: '/app/dashboard-preenchido.webp',
  checklist: '/app/checklist-1-humor.webp',
  bemEstar: '/app/bem-estar.webp',
};

export default function HeroSection() {
  const scrollToPricing = () => {
    const el = document.querySelector('#planos');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20 overflow-x-hidden"
      style={{ background: 'linear-gradient(180deg, #FFF5F8 0%, #fafaf9 100%)' }}
    >
      {/* Decorative blobs */}
      <div className="absolute top-20 -left-32 w-96 h-96 blob-primary opacity-60 pointer-events-none" />
      <div className="absolute bottom-10 right-0 w-80 h-80 blob-light opacity-40 pointer-events-none max-w-[50vw]" />
      <div className="absolute top-1/3 right-1/4 w-64 h-64 blob-primary opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="relative z-10"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#FAADC7]/20 text-[#B54182] rounded-full px-4 py-2 text-sm font-medium mb-6"
            >
              <Sparkles size={16} />
              Feito para mulheres, em cada fase
            </motion.div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Entenda sua{' '}
              <span className="relative inline-block">
                menopausa
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 8C40 2 80 2 100 6C120 10 160 4 198 8" stroke="#FAADC7" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
              .
              <br />
              Retome o{' '}
              <span className="gradient-text">controle</span>
              <br />
              do seu corpo.
            </h1>

            <p className="text-base md:text-lg text-stone-500 leading-relaxed mb-8 max-w-lg" style={{ fontFamily: 'var(--font-body)' }}>
              Cada mulher vive essa fase de um jeito. O MenoEase te ajuda
              a entender seus sintomas, acompanhar suas mudanças e ter suporte
              sempre que precisar.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => goToApp('hero')}
                className="text-white shadow-lg hover:shadow-[#B54182]/25 transition-all duration-300 rounded-full px-8 py-4 font-semibold text-lg"
                style={{ background: 'linear-gradient(135deg, #B54182, #BE5791)' }}
                data-testid="hero-cta-primary"
              >
                Comece Grátis Agora
              </motion.button>
              <motion.a
                whileHover={{ scale: 1.03 }}
                href="#sobre"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#sobre')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white text-[#B54182] border-2 border-[#FAADC7]/50 hover:border-[#FAADC7] hover:bg-[#FAADC7]/10 transition-all rounded-full px-8 py-4 font-medium text-center"
                data-testid="hero-cta-secondary"
              >
                Saiba Mais
              </motion.a>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-stone-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                7 dias grátis
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                Sem cartão de crédito
              </div>
            </div>
          </motion.div>

          {/* Phone mockups */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="relative min-h-[450px] lg:min-h-[580px]"
          >
            {/* Main phone - larger, centered at top */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute z-20 top-0 left-0 right-0 flex justify-center"
            >
              <div className="phone-mockup w-52 sm:w-56 md:w-64 animate-pulse-glow">
                <img
                  src={APP_SCREENSHOTS.dashboard}
                  alt="MenoEase Dashboard"
                  className="w-full"
                  loading="eager"
                />
              </div>
            </motion.div>

            {/* Left phone - smaller, offset below */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="absolute left-0 top-12 sm:top-14 z-10 hidden sm:block"
            >
              <div className="phone-mockup w-40 sm:w-44 md:w-48 opacity-85 transform -rotate-6">
                <img
                  src={APP_SCREENSHOTS.checklist}
                  alt="MenoEase Checklist"
                  className="w-full"
                  loading="eager"
                />
              </div>
            </motion.div>

            {/* Right phone - smaller, offset below */}
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute right-0 top-9 sm:top-10 z-10 hidden sm:block"
            >
              <div className="phone-mockup w-40 sm:w-44 md:w-48 opacity-85 transform rotate-6">
                <img
                  src={APP_SCREENSHOTS.bemEstar}
                  alt="MenoEase Bem-Estar"
                  className="w-full"
                  loading="eager"
                />
              </div>
            </motion.div>

            {/* Decorative circle - centered behind phones */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-80 h-80 rounded-full border-2 border-dashed border-[#FAADC7]/50 animate-spin" style={{ animationDuration: '30s' }} />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown size={20} className="text-[#BE5791]" />
      </motion.div>
    </section>
  );
}
